import { useState, useMemo, useRef, useEffect, useCallback, useId } from 'react';

export interface SearchableSelectOption {
  value: string;
  label: string;
}

export interface SearchableSelectProps {
  /** Options to display. Optionally prepend an empty option via emptyOption. */
  options: SearchableSelectOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  /** If set, show this option first (e.g. "All games") and allow selecting it (value ''). */
  emptyOption?: { value: ''; label: string };
  /** Input id for label association (a11y). */
  id?: string;
  /** Allow custom value by typing and blur/enter commit. */
  allowCustomValue?: boolean;
}

export function SearchableSelect({
  options,
  value,
  onChange,
  placeholder = 'Search...',
  disabled = false,
  className = '',
  emptyOption,
  id,
  allowCustomValue = false,
}: Readonly<SearchableSelectProps>) {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const listboxId = useId();
  const [menuRect, setMenuRect] = useState<{ top: number; left: number; width: number } | null>(
    null
  );

  const effectiveOptions = useMemo(() => {
    const list = emptyOption
      ? [{ value: '' as const, label: emptyOption.label }, ...options]
      : options;
    return list;
  }, [options, emptyOption]);

  const filteredOptions = useMemo(() => {
    if (!searchTerm.trim()) return effectiveOptions;
    const term = searchTerm.toLowerCase();
    return effectiveOptions.filter(opt => opt.label.toLowerCase().includes(term));
  }, [effectiveOptions, searchTerm]);

  const selectedOption = effectiveOptions.find(opt => opt.value === value);
  const displayValue = isOpen
    ? searchTerm
    : (selectedOption?.label ?? (allowCustomValue ? value : ''));

  const closeDropdown = useCallback(() => {
    setSearchTerm('');
    setIsOpen(false);
    setMenuRect(null);
  }, []);

  const updateMenuPosition = useCallback(() => {
    if (!inputRef.current) return;
    const rect = inputRef.current.getBoundingClientRect();
    setMenuRect({
      top: rect.bottom + window.scrollY,
      left: rect.left + window.scrollX,
      width: rect.width,
    });
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        closeDropdown();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, closeDropdown]);

  useEffect(() => {
    if (!isOpen) return;
    updateMenuPosition();
    const handleWindowChange = () => updateMenuPosition();
    window.addEventListener('resize', handleWindowChange);
    window.addEventListener('scroll', handleWindowChange, true);
    return () => {
      window.removeEventListener('resize', handleWindowChange);
      window.removeEventListener('scroll', handleWindowChange, true);
    };
  }, [isOpen, updateMenuPosition]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setIsOpen(true);
    updateMenuPosition();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      closeDropdown();
      (e.target as HTMLInputElement).blur();
    }
    if (e.key === 'Enter' && allowCustomValue && searchTerm.trim()) {
      onChange(searchTerm.trim());
      closeDropdown();
      return;
    }
    if (e.key === 'Enter' && filteredOptions.length === 1) {
      onChange(filteredOptions[0].value);
      closeDropdown();
    }
  };

  const handleSelect = (optionValue: string) => {
    onChange(optionValue);
    closeDropdown();
  };

  const handleFocus = () => setIsOpen(true);

  const handleBlur = () => {
    if (allowCustomValue && searchTerm.trim()) {
      onChange(searchTerm.trim());
    }
    setTimeout(closeDropdown, 180);
  };

  const showClear = Boolean(value) && !disabled && emptyOption != null;

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <div className="relative">
        <input
          id={id}
          ref={inputRef}
          type="text"
          role="combobox"
          aria-expanded={isOpen}
          aria-controls={isOpen ? listboxId : undefined}
          aria-autocomplete="list"
          value={displayValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={placeholder}
          disabled={disabled}
          className={`w-full bg-background border border-border rounded-md text-foreground transition-colors focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 ${
            showClear ? 'pl-3 pr-9 py-2' : 'px-3 py-2'
          } ${isOpen ? 'border-primary' : ''}`}
        />
        {showClear && (
          <button
            type="button"
            onClick={() => handleSelect('')}
            onMouseDown={e => e.preventDefault()}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-muted-foreground hover:text-foreground hover:bg-muted/50"
            aria-label="Clear selection"
          >
            <svg
              className="h-4 w-4"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {isOpen && (
        <ul
          id={listboxId}
          className="fixed z-50 bg-card border border-border rounded-md shadow-lg max-h-60 overflow-auto py-1"
          style={
            menuRect
              ? {
                  top: menuRect.top,
                  left: menuRect.left,
                  width: menuRect.width,
                }
              : undefined
          }
        >
          {filteredOptions.length === 0 ? (
            <li className="px-3 py-2 text-sm text-muted-foreground">No results</li>
          ) : (
            filteredOptions.map(opt => (
              <li key={opt.value === '' ? '__empty__' : opt.value}>
                <button
                  type="button"
                  onClick={() => handleSelect(opt.value)}
                  className={`w-full px-3 py-2 text-left text-sm cursor-pointer hover:bg-muted/50 text-foreground ${
                    value === opt.value ? 'bg-primary/10 text-primary font-medium' : ''
                  }`}
                >
                  {opt.label}
                </button>
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}
